<script setup>
// Sandbox: conversa de teste com o mesmo pipeline das conversas reais (ver AI.md).
import { ref, computed, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import AiPageLayout from '../components/AiPageLayout.vue';
import aiAPI from 'dashboard/api/ai';
import { useAiInboxSettings } from '../composables/useAiInboxSettings';

const { t } = useI18n();
const {
  selectedInboxId,
  selectedSetting,
  inboxOptions,
  providerConfigured,
  fetchSettings,
} = useAiInboxSettings();

const messages = ref([]);
const draft = ref('');
const isSending = ref(false);
const lastResult = ref(null);
const chatRef = ref(null);
// Andamento do agendamento pela agenda (opções oferecidas, horário escolhido); vai e volta com o servidor.
const schedulingState = ref({});

const schedulingEventLabels = computed(() => ({
  offered: t('AI.SANDBOX.SCHEDULING.OFFERED'),
  confirming: t('AI.SANDBOX.SCHEDULING.CONFIRMING'),
  booked: t('AI.SANDBOX.SCHEDULING.BOOKED'),
  handoff: t('AI.SANDBOX.SCHEDULING.HANDOFF'),
}));

const assistantName = computed(
  () => selectedSetting.value?.assistant_name || 'IA'
);
const labels = computed(() => ({
  status: {
    cited: t('AI.SANDBOX.STATUS.CITED'),
    context: t('AI.SANDBOX.STATUS.CONTEXT'),
    discarded: t('AI.SANDBOX.STATUS.DISCARDED'),
  },
  intent: {
    greeting: t('AI.SANDBOX.INTENTS.greeting'),
    thanks: t('AI.SANDBOX.INTENTS.thanks'),
    farewell: t('AI.SANDBOX.INTENTS.farewell'),
    smalltalk: t('AI.SANDBOX.INTENTS.smalltalk'),
    question: t('AI.SANDBOX.INTENTS.question'),
    scheduling: t('AI.SANDBOX.INTENTS.scheduling'),
    human_request: t('AI.SANDBOX.INTENTS.human_request'),
    clinical: t('AI.SANDBOX.INTENTS.clinical'),
    complaint: t('AI.SANDBOX.INTENTS.complaint'),
    emergency: t('AI.SANDBOX.INTENTS.emergency'),
    other: t('AI.SANDBOX.INTENTS.other'),
  },
  priority: {
    urgent: t('AI.SANDBOX.PRIORITIES.urgent'),
    high: t('AI.SANDBOX.PRIORITIES.high'),
    medium: t('AI.SANDBOX.PRIORITIES.medium'),
    low: t('AI.SANDBOX.PRIORITIES.low'),
  },
  collected: {
    nome: t('AI.SANDBOX.COLLECTED_FIELDS.nome'),
    especialidade_ou_medico: t(
      'AI.SANDBOX.COLLECTED_FIELDS.especialidade_ou_medico'
    ),
    convenio_ou_particular: t(
      'AI.SANDBOX.COLLECTED_FIELDS.convenio_ou_particular'
    ),
    preferencia_data_horario: t(
      'AI.SANDBOX.COLLECTED_FIELDS.preferencia_data_horario'
    ),
  },
}));

const collectedEntries = computed(() =>
  Object.entries(lastResult.value?.collected || {})
    .filter(([, value]) => value)
    .map(([field, value]) => ({
      field,
      text: `${labels.value.collected[field] || field}: ${value}`,
    }))
);

// PortVia: "Citado" e "Respondeu" na cor da marca (escala blue gerada pelo branding.yml).
const statusClasses = {
  cited: 'bg-n-blue-3 text-n-blue-11',
  context: 'bg-n-slate-3 text-n-slate-11',
  discarded: 'bg-n-slate-2 text-n-slate-10',
};
const priorityClasses = {
  urgent: 'text-n-ruby-11',
  high: 'text-n-amber-11',
  medium: 'text-n-slate-12',
};

const formatScore = score => Number(score).toFixed(3).replace('.', ',');

const scrollToBottom = async () => {
  await nextTick();
  if (chatRef.value) chatRef.value.scrollTop = chatRef.value.scrollHeight;
};

const send = async () => {
  const content = draft.value.trim();
  if (!content || isSending.value || !selectedInboxId.value) return;

  messages.value.push({ role: 'user', content });
  draft.value = '';
  isSending.value = true;
  scrollToBottom();
  try {
    const history = messages.value.map(({ role, content: text }) => ({
      role,
      content: text,
    }));
    const { data } = await aiAPI.runSandbox(
      selectedInboxId.value,
      history,
      schedulingState.value
    );
    lastResult.value = data;
    schedulingState.value = data.scheduling_state || {};
    messages.value.push({
      role: 'assistant',
      content: data.reply,
      handoff: data.handoff,
      emergency: data.emergency,
      note: data.handoff
        ? `${data.reason_label} — ${t('AI.SANDBOX.TRANSFERRED_NOTE')}`
        : '',
    });
  } catch (error) {
    messages.value.pop();
    draft.value = content;
    useAlert(error?.response?.data?.error || t('AI.SANDBOX.ERROR'));
  } finally {
    isSending.value = false;
    scrollToBottom();
  }
};

const clear = () => {
  messages.value = [];
  lastResult.value = null;
  draft.value = '';
  schedulingState.value = {};
};

onMounted(fetchSettings);
</script>

<template>
  <AiPageLayout
    :title="t('AI.SANDBOX.TITLE')"
    :description="t('AI.SANDBOX.DESCRIPTION')"
    :provider-warning="!providerConfigured"
    wide
  >
    <template #actions>
      <Button
        :label="t('AI.SANDBOX.CLEAR')"
        icon="i-lucide-eraser"
        variant="outline"
        color="slate"
        size="sm"
        :disabled="!messages.length || isSending"
        data-testid="ai-sandbox-clear"
        @click="clear"
      />
    </template>

    <div class="grid h-full gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
      <!-- Conversa -->
      <section
        class="flex flex-col min-h-[32rem] rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak overflow-hidden"
      >
        <!-- PortVia: com uma conexão só (o normal), não há o que escolher -->
        <div
          v-if="inboxOptions.length > 1"
          class="flex items-center gap-3 px-4 py-3 border-b border-n-weak"
        >
          <span class="text-label text-n-slate-11 shrink-0">{{
            t('AI.SANDBOX.INBOX')
          }}</span>
          <Select
            v-model="selectedInboxId"
            :options="inboxOptions"
            :disabled="isSending"
          />
        </div>

        <div
          ref="chatRef"
          class="flex flex-col flex-1 gap-3 p-4 overflow-y-auto"
          data-testid="ai-sandbox-chat"
        >
          <p
            v-if="!messages.length"
            class="m-auto text-body-para text-n-slate-10 text-center max-w-sm"
          >
            {{ t('AI.SANDBOX.EMPTY') }}
          </p>
          <template v-for="(message, index) in messages" :key="index">
            <div
              class="flex flex-col max-w-[80%] gap-1"
              :class="
                message.role === 'user'
                  ? 'self-end items-end'
                  : 'self-start items-start'
              "
            >
              <span class="text-label-small text-n-slate-10">
                {{
                  message.role === 'user' ? t('AI.SANDBOX.YOU') : assistantName
                }}
              </span>
              <div
                class="px-3 py-2 text-sm whitespace-pre-wrap rounded-2xl"
                :class="
                  message.role === 'user'
                    ? 'bg-n-brand text-white rounded-br-md'
                    : message.emergency
                      ? 'bg-n-ruby-3 text-n-ruby-12 rounded-bl-md'
                      : 'bg-n-slate-3 text-n-slate-12 rounded-bl-md'
                "
              >
                {{ message.content }}
              </div>
              <span
                v-if="message.handoff"
                class="inline-flex items-center gap-1 text-label-small text-n-amber-11"
              >
                <span class="i-lucide-user-round-check size-3" />
                {{ message.note }}
              </span>
            </div>
          </template>
          <div
            v-if="isSending"
            class="flex items-center gap-2 self-start text-label-small text-n-slate-10"
          >
            <span class="i-lucide-loader-circle size-3 animate-spin" />
            {{ t('AI.SANDBOX.THINKING') }}
          </div>
        </div>

        <form
          class="flex items-center gap-2 p-3 border-t border-n-weak"
          @submit.prevent="send"
        >
          <Input
            v-model="draft"
            class="flex-1"
            :placeholder="t('AI.SANDBOX.PLACEHOLDER')"
            :disabled="isSending || !selectedInboxId"
            data-testid="ai-sandbox-input"
          />
          <Button
            type="submit"
            :label="t('AI.SANDBOX.SEND')"
            icon="i-lucide-send-horizontal"
            :is-loading="isSending"
            :disabled="!draft.trim() || !selectedInboxId"
          />
        </form>
      </section>

      <!-- Detalhes -->
      <aside
        class="flex flex-col gap-4 p-4 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak overflow-y-auto"
        data-testid="ai-sandbox-details"
      >
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('AI.SANDBOX.DETAILS.TITLE') }}
        </h2>
        <p v-if="!lastResult" class="text-body-para text-n-slate-10">
          {{ t('AI.SANDBOX.DETAILS.EMPTY') }}
        </p>

        <template v-else>
          <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt class="text-n-slate-11">
              {{ t('AI.SANDBOX.DETAILS.DECISION') }}
            </dt>
            <dd
              class="font-medium"
              :class="lastResult.handoff ? 'text-n-amber-11' : 'text-n-blue-11'"
            >
              {{
                lastResult.handoff
                  ? t('AI.SANDBOX.DETAILS.TRANSFERRED')
                  : t('AI.SANDBOX.DETAILS.ANSWERED')
              }}
            </dd>
            <template v-if="lastResult.handoff">
              <dt class="text-n-slate-11">
                {{ t('AI.SANDBOX.DETAILS.REASON') }}
              </dt>
              <dd class="text-n-slate-12">{{ lastResult.reason_label }}</dd>
              <dt class="text-n-slate-11">
                {{ t('AI.SANDBOX.DETAILS.PRIORITY') }}
              </dt>
              <dd :class="priorityClasses[lastResult.priority]">
                {{ labels.priority[lastResult.priority] }}
              </dd>
              <dt class="text-n-slate-11">
                {{ t('AI.SANDBOX.DETAILS.OUT_OF_HOURS') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  lastResult.out_of_hours
                    ? t('AI.SANDBOX.DETAILS.YES')
                    : t('AI.SANDBOX.DETAILS.NO')
                }}
              </dd>
            </template>
            <dt class="text-n-slate-11">
              {{ t('AI.SANDBOX.DETAILS.INTENT') }}
            </dt>
            <dd class="text-n-slate-12">
              {{ labels.intent[lastResult.intent] }}
            </dd>
            <template v-if="lastResult.scheduling_event">
              <dt class="text-n-slate-11">
                {{ t('AI.SANDBOX.SCHEDULING.TITLE') }}
              </dt>
              <dd class="text-n-slate-12" data-testid="ai-sandbox-scheduling">
                {{ schedulingEventLabels[lastResult.scheduling_event] }}
              </dd>
            </template>
          </dl>

          <div
            v-if="lastResult.appointment"
            class="flex flex-col gap-1 px-3 py-2 rounded-lg bg-n-teal-2 outline outline-1 outline-n-teal-5"
          >
            <span class="text-sm font-medium text-n-teal-11">
              {{ t('AI.SANDBOX.SCHEDULING.SIMULATED') }}
            </span>
            <span class="text-sm text-n-slate-12">
              {{ lastResult.appointment.professional_name }} ·
              {{ lastResult.appointment.when }}
            </span>
            <span class="text-label-small text-n-slate-11">
              {{ t('AI.SANDBOX.SCHEDULING.SIMULATED_HELP') }}
            </span>
          </div>

          <div v-if="collectedEntries.length" class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('AI.SANDBOX.DETAILS.COLLECTED') }}
            </h3>
            <ul class="text-sm text-n-slate-12">
              <li v-for="entry in collectedEntries" :key="entry.field">
                {{ entry.text }}
              </li>
            </ul>
          </div>

          <div
            v-if="lastResult.handoff && lastResult.summary"
            class="flex flex-col gap-1"
          >
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('AI.SANDBOX.DETAILS.SUMMARY') }}
            </h3>
            <p class="text-sm text-n-slate-12">{{ lastResult.summary }}</p>
          </div>

          <div
            v-if="
              lastResult.handoff &&
              lastResult.llm_reply &&
              lastResult.llm_reply !== lastResult.reply
            "
            class="flex flex-col gap-1"
          >
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('AI.SANDBOX.DETAILS.MODEL_LLM_TEXT') }}
            </h3>
            <p class="text-sm italic text-n-slate-11">
              {{ lastResult.llm_reply }}
            </p>
          </div>

          <div v-if="lastResult.error" class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-ruby-11">
              {{ t('AI.SANDBOX.DETAILS.ERROR') }}
            </h3>
            <p class="text-sm text-n-ruby-11 break-words">
              {{ lastResult.error }}
            </p>
          </div>

          <p v-if="lastResult.model" class="text-label-small text-n-slate-10">
            {{
              t('AI.SANDBOX.DETAILS.META', {
                model: lastResult.model,
                latency: lastResult.latency_ms,
              })
            }}
          </p>

          <div class="flex flex-col gap-2" data-testid="ai-sandbox-sources">
            <div class="flex flex-col">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ t('AI.SANDBOX.DETAILS.SOURCES') }}
              </h3>
              <span class="text-label-small text-n-slate-10">
                {{
                  t('AI.SANDBOX.DETAILS.SOURCES_HELP', {
                    min: formatScore(lastResult.min_similarity),
                    k: lastResult.retrieval_k,
                  })
                }}
              </span>
            </div>
            <p
              v-if="!lastResult.sources.length"
              class="text-sm text-n-slate-10"
            >
              {{ t('AI.SANDBOX.DETAILS.NO_SOURCES') }}
            </p>
            <details
              v-for="source in lastResult.sources"
              :key="source.chunk_id"
              class="px-3 py-2 rounded-lg outline outline-1 outline-n-weak"
              :class="{ 'opacity-70': source.status === 'discarded' }"
            >
              <summary
                class="flex flex-wrap items-center gap-2 cursor-pointer list-none"
              >
                <span
                  class="px-1.5 py-0.5 rounded text-label-small"
                  :class="statusClasses[source.status]"
                >
                  {{ labels.status[source.status] }}
                </span>
                <span class="text-label text-n-slate-12 truncate">{{
                  source.document_title
                }}</span>
                <span
                  class="ml-auto text-label-small text-n-slate-11 tabular-nums"
                >
                  {{
                    t('AI.SANDBOX.DETAILS.SCORE', {
                      score: formatScore(source.score),
                    })
                  }}
                </span>
              </summary>
              <p class="mt-2 text-sm whitespace-pre-wrap text-n-slate-11">
                {{ source.content }}
              </p>
            </details>
          </div>
        </template>
      </aside>
    </div>
  </AiPageLayout>
</template>
